import { useCallback, useRef } from 'react'

import { TriageSearch } from '../../../models/searchForCare/SearchForCare'

type UseBackToTriageArgs = {
  goBack?: () => void
  search?: TriageSearch
  /**
   * Optional escape hatch for cases where the full `search` object isn't available in state
   * but we still know the currently-selected search id (e.g. during navigation transitions).
   */
  searchId?: string
  cloneTriageSearch: (searchId: string) => unknown
  shouldCreateDuplicateInProgressSearch?: boolean
}

export const useBackToTriage = ({
  goBack,
  search,
  searchId: searchIdOverride,
  cloneTriageSearch,
  shouldCreateDuplicateInProgressSearch = true,
}: UseBackToTriageArgs) => {
  const searchId = searchIdOverride ?? search?.id
  const lastClonedSearchIdRef = useRef<string | undefined>(undefined)

  const handleBackPress = useCallback(async () => {
    // Create a duplicate in_progress search when going back to triage.
    // Important: start the clone request *before* navigation. Some navigation implementations
    // (notably web/history-based) can interrupt work scheduled after a back navigation.
    const shouldClone =
      shouldCreateDuplicateInProgressSearch && !!searchId && lastClonedSearchIdRef.current !== searchId
    const clonePromise = shouldClone ? cloneTriageSearch(searchId!) : undefined

    if (shouldClone) {
      lastClonedSearchIdRef.current = searchId
    }

    goBack?.()

    if (clonePromise !== undefined) {
      await clonePromise
    }
  }, [goBack, cloneTriageSearch, searchId, shouldCreateDuplicateInProgressSearch])

  return handleBackPress
}
